// Step 5 - the sending and warmup profile, and the run's tags.
//
// The form is prefilled from `run.params` when the run has been here before, and
// from the canonical profile for the run's sequencer when it has not. Each
// canonical object is a shared module-level constant: it is READ to seed the form
// and never written to, or every later run in the session would start from the
// last operator's edits.
//
// The whole numeric/toggle form is per-sequencer - PlusVibe, Instantly,
// Smartlead and Bison express the same knobs under different field names - so
// this file dispatches to one of four form components by run.sequencer (the
// Instantly and Bison forms live in their own files). The tags panel underneath
// is provider agnostic (a tag is a tag in any workspace), so it is one shared
// component, TagsPanel (./TagsPanel.tsx), rendered by all four.
//
// TagsPanel is the one part of the wizard that fetches. GET /:id/tags is a
// read-only proxy over the customer's workspace - it lists tags and never creates
// one, so browsing it leaves nothing behind in their account - and the list is
// only wanted while this step is on screen.

import { useState } from 'react';

import {
  CANONICAL_SQ_PARAMS,
  CANONICAL_SQ_SMARTLEAD_PARAMS,
  sqApi,
  sqErrorMessage,
  type SqPlusVibeParams,
  type SqSmartleadParams,
} from '../sequencerApi';
import type { SqStepProps } from './shared';
import { TagsPanel } from './TagsPanel';
import { StepParamsBison } from './StepParamsBison';
import { StepParamsInstantly } from './StepParamsInstantly';

/** Matches nonNegativeInt in the params route. Kept in step with it so a value
 *  the server would refuse is caught here, next to the box that holds it. */
const MAX_INT = 10_000;

/** A whole integer inside the field's own [min, max] range, or null. The regex is
 *  deliberate: Number('') is 0 and Number('3.5') is 3.5, and both would otherwise
 *  pass silently as something the operator did not type. `min` defaults to 0, so
 *  the PlusVibe fields (which carry no floor) are unaffected. */
function parseNum(raw: string, max: number, min = 0): number | null {
  const t = raw.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return Number.isInteger(n) && n >= min && n <= max ? n : null;
}

/* ─── PlusVibe form fields ───────────────────────────────────────────────── */

type PvNumKey =
  | 'min_interval'
  | 'daily_limit'
  | 'camp_ramup_start'
  | 'camp_rampup_increment'
  | 'warmup_daily_limit'
  | 'warmup_rampup_start'
  | 'warmup_rampup_increment'
  | 'reply_rate_pct';

type PvToggleKey = 'enable_camp_rampup' | 'enable_warmup' | 'enable_warmup_rampup';

const PV_NUM_FIELDS: { key: PvNumKey; label: string; hint: string; max: number; suffix?: string }[] =
  [
    {
      key: 'min_interval',
      label: 'Minimum interval',
      hint: 'Minutes PlusVibe waits between two sends from this mailbox.',
      max: MAX_INT,
    },
    {
      key: 'daily_limit',
      label: 'Daily limit',
      hint: 'Campaign emails per day, per mailbox.',
      max: MAX_INT,
    },
    {
      key: 'camp_ramup_start',
      label: 'Campaign ramp-up start',
      hint: 'Day-one campaign volume when campaign ramp-up is on.',
      max: MAX_INT,
    },
    {
      key: 'camp_rampup_increment',
      label: 'Campaign ramp-up increment',
      hint: 'Added to the campaign volume each day.',
      max: MAX_INT,
    },
    {
      key: 'warmup_daily_limit',
      label: 'Warmup daily limit',
      hint: 'Warmup emails per day, per mailbox.',
      max: MAX_INT,
    },
    {
      key: 'warmup_rampup_start',
      label: 'Warmup ramp-up start',
      hint: 'Day-one warmup volume when warmup ramp-up is on.',
      max: MAX_INT,
    },
    {
      key: 'warmup_rampup_increment',
      label: 'Warmup ramp-up increment',
      hint: 'Added to the warmup volume each day.',
      max: MAX_INT,
    },
    {
      key: 'reply_rate_pct',
      label: 'Reply rate',
      hint: 'Share of warmup mail that gets a reply.',
      max: 100,
      suffix: '%',
    },
  ];

const PV_TOGGLES: { key: PvToggleKey; label: string; hint: string }[] = [
  {
    key: 'enable_camp_rampup',
    label: 'Campaign ramp-up',
    hint: 'Start campaigns low and climb to the daily limit.',
  },
  {
    key: 'enable_warmup',
    label: 'Warmup',
    hint: 'Run PlusVibe warmup on every connected mailbox.',
  },
  {
    key: 'enable_warmup_rampup',
    label: 'Warmup ramp-up',
    hint: 'Start warmup low and climb to the warmup daily limit.',
  },
];

/* ─── Smartlead form fields ──────────────────────────────────────────────── */

type SlNumKey =
  | 'max_email_per_day'
  | 'time_to_wait_in_mins'
  | 'total_warmup_per_day'
  | 'daily_rampup'
  | 'reply_rate_pct';

type SlToggleKey = 'enable_warmup' | 'enable_warmup_rampup';

// Bounds mirror smartleadParamsSchema in the params route, so a value the server
// would refuse is caught here next to the box that holds it.
const SL_NUM_FIELDS: {
  key: SlNumKey;
  label: string;
  hint: string;
  min: number;
  max: number;
  suffix?: string;
}[] = [
  {
    key: 'max_email_per_day',
    label: 'Daily limit',
    hint: 'Campaign emails per day, per mailbox.',
    min: 0,
    max: MAX_INT,
  },
  {
    key: 'time_to_wait_in_mins',
    label: 'Sending gap',
    hint: 'Minutes Smartlead waits between two sends from this mailbox.',
    min: 0,
    max: MAX_INT,
    suffix: 'min',
  },
  {
    key: 'total_warmup_per_day',
    label: 'Warmup daily total',
    hint: 'Warmup emails per day, per mailbox. Smartlead accepts 1-50.',
    min: 1,
    max: 50,
  },
  {
    key: 'daily_rampup',
    label: 'Daily rampup step',
    hint: 'Added to the warmup volume each day when warmup ramp-up is on. Smartlead accepts 5-20.',
    min: 5,
    max: 20,
  },
  {
    key: 'reply_rate_pct',
    label: 'Reply rate',
    hint: 'Share of warmup mail that gets a reply. Smartlead accepts 20-100.',
    min: 20,
    max: 100,
    suffix: '%',
  },
];

const SL_TOGGLES: { key: SlToggleKey; label: string; hint: string }[] = [
  {
    key: 'enable_warmup',
    label: 'Warmup',
    hint: 'Run Smartlead warmup on every connected mailbox.',
  },
  {
    key: 'enable_warmup_rampup',
    label: 'Warmup ramp-up',
    hint: 'Start warmup low and climb to the warmup daily total.',
  },
];

/* ─── PlusVibe form ──────────────────────────────────────────────────────── */

function PlusVibeParams({ runId, detail, reload, goToStep, toast }: SqStepProps): JSX.Element {
  const { run } = detail;

  // Seeded once, from the run's own saved profile or the canonical one. Every
  // read below builds a NEW object; CANONICAL_SQ_PARAMS itself is never touched.
  const [nums, setNums] = useState<Record<PvNumKey, string>>(() => {
    const seed = (run.params as SqPlusVibeParams | null) ?? CANONICAL_SQ_PARAMS;
    return {
      min_interval: String(seed.min_interval),
      daily_limit: String(seed.daily_limit),
      camp_ramup_start: String(seed.camp_ramup_start),
      camp_rampup_increment: String(seed.camp_rampup_increment),
      warmup_daily_limit: String(seed.warmup_daily_limit),
      warmup_rampup_start: String(seed.warmup_rampup_start),
      warmup_rampup_increment: String(seed.warmup_rampup_increment),
      reply_rate_pct: String(seed.reply_rate_pct),
    };
  });

  const [toggles, setToggles] = useState<Record<PvToggleKey, 'yes' | 'no'>>(() => {
    const seed = (run.params as SqPlusVibeParams | null) ?? CANONICAL_SQ_PARAMS;
    return {
      enable_camp_rampup: seed.enable_camp_rampup,
      enable_warmup: seed.enable_warmup,
      enable_warmup_rampup: seed.enable_warmup_rampup,
    };
  });

  const [existingIds, setExistingIds] = useState<string[]>(() => [
    ...(run.params?.extra_tags.existing_ids ?? []),
  ]);
  const [newNames, setNewNames] = useState<string[]>(() => [
    ...(run.params?.extra_tags.new_names ?? []),
  ]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(): Promise<void> {
    const parsed = {} as Record<PvNumKey, number>;
    for (const f of PV_NUM_FIELDS) {
      const value = parseNum(nums[f.key], f.max);
      if (value === null) {
        setError(`${f.label} must be a whole number between 0 and ${f.max}.`);
        return;
      }
      parsed[f.key] = value;
    }

    const params: SqPlusVibeParams = {
      ...parsed,
      ...toggles,
      // `resolved_ids` is deliberately absent: the params route rejects it and
      // Start is the only writer, from a live tag listing.
      extra_tags: { existing_ids: [...existingIds], new_names: [...newNames] },
    };

    setBusy(true);
    setError(null);
    try {
      await sqApi.setParams(runId, params);
      await reload();
      await goToStep(6);
    } catch (err) {
      setError(sqErrorMessage(err));
      toast('error', sqErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="sq-step">
      <div className="sq-step-head">
        <h2>Step 5 - Parameters</h2>
        <p>
          The sending and warmup profile pushed to every mailbox in this run, prefilled with our
          standard values. Change one only when the customer's order says so.
        </p>
      </div>
      <div className="sq-step-body">
        {error && <div className="sq-notice sq-notice--err">{error}</div>}

        <div className="sq-numgrid">
          {PV_NUM_FIELDS.map((f) => (
            <div className="sq-field" key={f.key}>
              <label className="sq-field-label" htmlFor={`sq-param-${f.key}`}>
                {f.label}
                {f.suffix ? ` (${f.suffix})` : ''}
              </label>
              <input
                id={`sq-param-${f.key}`}
                className="sq-input sq-mono"
                type="number"
                inputMode="numeric"
                min={0}
                max={f.max}
                step={1}
                value={nums[f.key]}
                onChange={(e) => setNums({ ...nums, [f.key]: e.target.value })}
                disabled={busy}
              />
              <span className="sq-hint">{f.hint}</span>
            </div>
          ))}
        </div>

        <div className="sq-togglegrid">
          {PV_TOGGLES.map((t) => (
            <div className="sq-field" key={t.key}>
              <span className="sq-field-label">{t.label}</span>
              <div className="sq-toggle">
                {(['yes', 'no'] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    className={`sq-toggle-btn${toggles[t.key] === v ? ' sq-toggle-btn--on' : ''}`}
                    onClick={() => setToggles({ ...toggles, [t.key]: v })}
                    disabled={busy}
                    aria-pressed={toggles[t.key] === v}
                  >
                    {v === 'yes' ? 'Yes' : 'No'}
                  </button>
                ))}
              </div>
              <span className="sq-hint">{t.hint}</span>
            </div>
          ))}
        </div>

        <TagsPanel
          runId={runId}
          tagName={run.tag_name}
          busy={busy}
          existingIds={existingIds}
          setExistingIds={setExistingIds}
          newNames={newNames}
          setNewNames={setNewNames}
        />

        <div className="sq-actions">
          <button
            type="button"
            className="sq-btn sq-btn--primary"
            onClick={() => void save()}
            disabled={busy}
          >
            {busy ? 'Saving...' : 'Save & continue'}
          </button>
        </div>
      </div>
    </section>
  );
}

/* ─── Smartlead form ─────────────────────────────────────────────────────── */

function SmartleadParams({ runId, detail, reload, goToStep, toast }: SqStepProps): JSX.Element {
  const { run, items } = detail;

  // The dispatcher only mounts this for a Smartlead run, so a non-null params is a
  // Smartlead params; the cast expresses that the discriminated union already
  // guarantees. CANONICAL_SQ_SMARTLEAD_PARAMS is read to seed, never mutated.
  const seed = (run.params as SqSmartleadParams | null) ?? CANONICAL_SQ_SMARTLEAD_PARAMS;

  const [nums, setNums] = useState<Record<SlNumKey, string>>(() => ({
    max_email_per_day: String(seed.max_email_per_day),
    time_to_wait_in_mins: String(seed.time_to_wait_in_mins),
    total_warmup_per_day: String(seed.total_warmup_per_day),
    daily_rampup: String(seed.daily_rampup),
    reply_rate_pct: String(seed.reply_rate_pct),
  }));

  const [toggles, setToggles] = useState<Record<SlToggleKey, 'yes' | 'no'>>(() => ({
    enable_warmup: seed.enable_warmup,
    enable_warmup_rampup: seed.enable_warmup_rampup,
  }));

  const [trackingEnabled, setTrackingEnabled] = useState<boolean>(() => seed.tracking.enabled);
  const [trackingPrefix, setTrackingPrefix] = useState<string>(() => seed.tracking.prefix);

  const [existingIds, setExistingIds] = useState<string[]>(() => [
    ...(run.params?.extra_tags.existing_ids ?? []),
  ]);
  const [newNames, setNewNames] = useState<string[]>(() => [
    ...(run.params?.extra_tags.new_names ?? []),
  ]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The tracking subdomain is the prefix joined to each mailbox's OWN domain, so
  // the preview is built off the first account in the paste. Empty list before
  // the accounts step, so fall back to a placeholder domain.
  const firstEmail = items[0]?.email ?? '';
  const at = firstEmail.lastIndexOf('@');
  const firstDomain = at >= 0 ? firstEmail.slice(at + 1) : '';
  const previewDomain = firstDomain || 'your-domain.com';
  const prefix = trackingPrefix.trim();
  const trackingPreview = prefix ? `${prefix}.${previewDomain}` : previewDomain;

  async function save(): Promise<void> {
    const parsed = {} as Record<SlNumKey, number>;
    for (const f of SL_NUM_FIELDS) {
      const value = parseNum(nums[f.key], f.max, f.min);
      if (value === null) {
        setError(`${f.label} must be a whole number between ${f.min} and ${f.max}.`);
        return;
      }
      parsed[f.key] = value;
    }

    const params: SqSmartleadParams = {
      max_email_per_day: parsed.max_email_per_day,
      time_to_wait_in_mins: parsed.time_to_wait_in_mins,
      enable_warmup: toggles.enable_warmup,
      total_warmup_per_day: parsed.total_warmup_per_day,
      enable_warmup_rampup: toggles.enable_warmup_rampup,
      daily_rampup: parsed.daily_rampup,
      reply_rate_pct: parsed.reply_rate_pct,
      tracking: { enabled: trackingEnabled, prefix },
      // `resolved_ids` is deliberately absent: the params route rejects it and
      // Start is the only writer, from a live tag listing.
      extra_tags: { existing_ids: [...existingIds], new_names: [...newNames] },
    };

    setBusy(true);
    setError(null);
    try {
      await sqApi.setParams(runId, params);
      await reload();
      await goToStep(6);
    } catch (err) {
      setError(sqErrorMessage(err));
      toast('error', sqErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="sq-step">
      <div className="sq-step-head">
        <h2>Step 5 - Parameters</h2>
        <p>
          The sending and warmup profile pushed to every mailbox in this run, prefilled with our
          standard values. Change one only when the customer's order says so.
        </p>
      </div>
      <div className="sq-step-body">
        {error && <div className="sq-notice sq-notice--err">{error}</div>}

        <div className="sq-numgrid">
          {SL_NUM_FIELDS.map((f) => (
            <div className="sq-field" key={f.key}>
              <label className="sq-field-label" htmlFor={`sq-slparam-${f.key}`}>
                {f.label}
                {f.suffix ? ` (${f.suffix})` : ''}
              </label>
              <input
                id={`sq-slparam-${f.key}`}
                className="sq-input sq-mono"
                type="number"
                inputMode="numeric"
                min={f.min}
                max={f.max}
                step={1}
                value={nums[f.key]}
                onChange={(e) => setNums({ ...nums, [f.key]: e.target.value })}
                disabled={busy}
              />
              <span className="sq-hint">{f.hint}</span>
            </div>
          ))}
        </div>

        <div className="sq-togglegrid">
          {SL_TOGGLES.map((t) => (
            <div className="sq-field" key={t.key}>
              <span className="sq-field-label">{t.label}</span>
              <div className="sq-toggle">
                {(['yes', 'no'] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    className={`sq-toggle-btn${toggles[t.key] === v ? ' sq-toggle-btn--on' : ''}`}
                    onClick={() => setToggles({ ...toggles, [t.key]: v })}
                    disabled={busy}
                    aria-pressed={toggles[t.key] === v}
                  >
                    {v === 'yes' ? 'Yes' : 'No'}
                  </button>
                ))}
              </div>
              <span className="sq-hint">{t.hint}</span>
            </div>
          ))}
        </div>

        <div className="sq-field">
          <span className="sq-field-label">Tracking domain</span>
          <label className="sq-check">
            <input
              type="checkbox"
              checked={trackingEnabled}
              onChange={(e) => setTrackingEnabled(e.target.checked)}
              disabled={busy}
            />
            <span>Track opens and clicks through a per-mailbox tracking subdomain</span>
          </label>
          <span className="sq-hint">
            Smartlead rewrites links through a tracking subdomain built on each mailbox's own
            domain. Off sends without a tracking domain.
          </span>
        </div>

        <div className="sq-field">
          <label className="sq-field-label" htmlFor="sq-sl-tracking-prefix">
            Tracking subdomain prefix
          </label>
          <input
            id="sq-sl-tracking-prefix"
            className="sq-input sq-mono"
            type="text"
            value={trackingPrefix}
            maxLength={63}
            placeholder="trk"
            spellCheck={false}
            autoComplete="off"
            onChange={(e) => setTrackingPrefix(e.target.value)}
            disabled={busy || !trackingEnabled}
          />
          <span className="sq-hint">
            Preview: <strong className="sq-mono">{trackingPreview}</strong> - the prefix joined to
            each mailbox's own domain.
          </span>
        </div>

        <TagsPanel
          runId={runId}
          tagName={run.tag_name}
          busy={busy}
          existingIds={existingIds}
          setExistingIds={setExistingIds}
          newNames={newNames}
          setNewNames={setNewNames}
        />

        <div className="sq-actions">
          <button
            type="button"
            className="sq-btn sq-btn--primary"
            onClick={() => void save()}
            disabled={busy}
          >
            {busy ? 'Saving...' : 'Save & continue'}
          </button>
        </div>
      </div>
    </section>
  );
}

/* ─── Dispatcher ─────────────────────────────────────────────────────────── */

export function StepParams(props: SqStepProps): JSX.Element {
  const sequencer = props.detail.run.sequencer;
  if (sequencer === 'instantly') return <StepParamsInstantly {...props} />;
  if (sequencer === 'bison') return <StepParamsBison {...props} />;
  return sequencer === 'smartlead' ? (
    <SmartleadParams {...props} />
  ) : (
    <PlusVibeParams {...props} />
  );
}
